import React, { useState, useEffect } from 'react';
import { FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      position: 'CTO, Tech Solutions Pvt Ltd',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80',
      rating: 5,
      text: 'Astrix Infonet has been instrumental in our digital transformation journey. Their Microsoft licensing solutions saved us significant costs while ensuring compliance. Excellent support team!',
    },
    {
      name: 'Priya Sharma',
      position: 'IT Director, Global Enterprises',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80',
      rating: 5,
      text: 'We migrated our entire infrastructure to Azure with Astrix Infonet. The transition was seamless, and their 24/7 support has been exceptional. Highly recommend their cloud services!',
    },
    {
      name: 'Amit Patel',
      position: 'Managing Director, Patel Industries',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&q=80',
      rating: 5,
      text: 'The cybersecurity solutions provided by Astrix Infonet have given us peace of mind. Their team identified vulnerabilities we never knew existed and implemented robust security measures.',
    },
    {
      name: 'Sneha Reddy',
      position: 'Operations Head, Reddy Corp',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80',
      rating: 5,
      text: 'As a Microsoft Silver Partner, Astrix Infonet delivered genuine products at competitive prices. Their expertise in volume licensing helped us optimize our software investments.',
    },
    {
      name: 'Vikram Singh',
      position: 'CEO, Singh Technologies',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80',
      rating: 5,
      text: 'Outstanding IT consulting services! Astrix Infonet understood our business needs perfectly and provided tailored solutions that improved our operational efficiency by 40%.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-orange-400 font-semibold text-lg">Testimonials</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-blue-200 max-w-2xl mx-auto text-lg">
            Don't just take our word for it. Here's what our valued clients have to say about our services.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors z-10"
          >
            <FaChevronLeft size={24} />
          </button>
          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors z-10"
          >
            <FaChevronRight size={24} />
          </button>

          {/* Testimonial Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 md:p-12">
            <FaQuoteLeft className="text-4xl text-orange-400 mb-6" />

            <div className="min-h-[150px]">
              <p className="text-lg md:text-xl leading-relaxed mb-8">
                {testimonials[currentIndex].text}
              </p>
            </div>

            {/* Rating */}
            <div className="flex items-center mb-6">
              {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                <FaStar key={i} className="text-yellow-400 mr-1" />
              ))}
            </div>

            {/* Author */}
            <div className="flex items-center">
              <img
                src={testimonials[currentIndex].image}
                alt={testimonials[currentIndex].name}
                className="w-16 h-16 rounded-full object-cover border-4 border-orange-400"
              />
              <div className="ml-4">
                <h4 className="font-bold text-lg">{testimonials[currentIndex].name}</h4>
                <p className="text-blue-200">{testimonials[currentIndex].position}</p>
              </div>
            </div>
          </div>

          {/* Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-orange-400 w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <h3 className="text-3xl font-bold text-orange-400 mb-2">4.9/5</h3>
            <p className="text-blue-200">Customer Rating</p>
          </div>
          <div>
            <h3 className="text-3xl font-bold text-orange-400 mb-2">500+</h3>
            <p className="text-blue-200">Happy Customers</p>
          </div>
          <div>
            <h3 className="text-3xl font-bold text-orange-400 mb-2">98%</h3>
            <p className="text-blue-200">Client Retention</p>
          </div>
          <div>
            <h3 className="text-3xl font-bold text-orange-400 mb-2">24/7</h3>
            <p className="text-blue-200">Support Available</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
